import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import { AlertSource, AlertType } from 'pages/AlertManagement/constants'

import axiosBaseQuery from 'services/axiosBaseQuery'
import { GenericResponse, TableRequestV3, TableResponse } from 'services/types'
import { AlertSettingDetailResponse, AlertSettingsLimit } from './types'
import {
  FilterRequestList,
  SortRequest,
  TableSettingsRequest,
} from '@evrekadev/evreka-ui-components'
import { sendExportNotification } from 'utils'
import { reportsApi } from 'services/ReportManagement/reports'

export const alertApi = createApi({
  reducerPath: 'alertApi',
  tagTypes: ['AlertSettings', 'Alerts', 'AlertSettingsDetail', 'AlertRelatedVehicles'],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/alert-management/clients/'}`,
  }),
  endpoints: (builder) => ({
    getAlertSettingList: builder.query<TableResponse<{}>, TableRequestV3>({
      query: ({ clientId, ...data }) => ({
        url: `${clientId}/alert_setting/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['AlertSettings'],
    }),
    addAlertSettings: builder.mutation<
      Omit<GenericResponse, 'success'> & { id: string },
      {
        clientId: string
        type: AlertType
        source_module: AlertSource
        limit: AlertSettingsLimit
        data_count?: number
        user_list: Array<{ min: number; max: number; user_ids: number[] }>
        is_device_alert: boolean
        linked_objects: Array<{
          object_id: string
          object_name: string
          model: number
        }>
      }
    >({
      query: ({ clientId, ...data }) => ({
        url: `${clientId}/alert_setting/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AlertSettings', 'AlertRelatedVehicles'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          console.log(err)
        }
      },
    }),
    getAlertList: builder.query<TableResponse<{}>, TableRequestV3>({
      query: ({ clientId, ...data }) => ({
        url: `${clientId}/alert/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['Alerts'],
    }),
    editAlertSettings: builder.mutation<
      GenericResponse,
      {
        clientId: string
        id: string
        user_list: Array<{ min: number; max: number; user_ids: number[] }>
        linked_objects: Array<{
          object_id: string
          object_name: string
          model: number
        }>
        limit?: AlertSettingsLimit
      }
    >({
      query: ({ clientId, id, ...data }) => ({
        url: `${clientId}/alert_setting/${id}/`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['AlertSettings', 'AlertSettingsDetail', 'AlertRelatedVehicles'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          console.log(err)
        }
      },
    }),
    deleteAlertSettings: builder.mutation<GenericResponse, { clientId: string; id: string }>({
      query: ({ clientId, id }) => ({
        url: `${clientId}/alert_setting/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AlertSettings', 'AlertRelatedVehicles'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          console.log(err)
        }
      },
    }),
    getAlertCount: builder.query<GenericResponse & { count: number }, { clientId: string }>({
      query: ({ clientId }) => ({
        url: `${clientId}/alert/alert_count/`,
        method: 'GET',
      }),
    }),
    getAlertSettingById: builder.query<
      AlertSettingDetailResponse,
      { clientId: string; id: string }
    >({
      query: ({ clientId, id }) => ({
        url: `${clientId}/alert_setting/${id}/`,
        method: 'GET',
      }),
      providesTags: ['AlertSettingsDetail'],
    }),
    getAlertSettingRelatedVehicles: builder.query<
      Array<{ value: string; label: string; plate: string; linked: boolean }>,
      { clientId: string; alert_type: AlertType }
    >({
      query: ({ clientId, alert_type }) => ({
        url: `${clientId}/alert/related_vehicles/`,
        method: 'GET',
        params: {
          alert_type,
        },
      }),
      providesTags: ['AlertRelatedVehicles'],
    }),
    exportAlert: builder.mutation<
      GenericResponse,
      {
        clientId: string
        filters: FilterRequestList
        sort: SortRequest
        columns: TableSettingsRequest
      }
    >({
      query: ({ clientId, ...data }) => ({
        url: `${clientId}/alert/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {
          console.error('Error exporting alert:', error)
        }
      },
    }),
  }),
})

export const {
  useGetAlertSettingListQuery,
  useAddAlertSettingsMutation,
  useGetAlertListQuery,
  useEditAlertSettingsMutation,
  useDeleteAlertSettingsMutation,
  useGetAlertCountQuery,
  useGetAlertSettingByIdQuery,
  useGetAlertSettingRelatedVehiclesQuery,
  useExportAlertMutation,
} = alertApi
